import { useEffect, useRef, useState, type AnimationEvent, type FC } from 'react';
import { useIntl } from 'dibs-react-intl';

import { searchBarMessages } from './searchBarMessages.ts';

import classnames from 'classnames';
import styles from './SearchBarRotatingPlaceholder.module.css';
import dibsCss from 'dibs-css';

const rotatingTerms = [
    'Aquamarine Rings',
    'Oval Dining Table',
    'Cartier Watches',
    'Chanel Bag',
    'Ralph Lauren Furniture',
    'Emerald Earrings',
    'Picasso',
    'Chandeliers',
    'Eames Lounge Chair',
];

const randomIndex = Math.floor(Math.random() * rotatingTerms.length);

/** Duration of the overlapping enter animation — keep in sync with CSS. */
const PLACEHOLDER_TRANSITION_MS = 1200;
/** Time the placeholder stays fully visible between transitions. */
const PLACEHOLDER_PAUSE_MS = 3000;
/** Interval between rotating-term changes: pause + simultaneous exit/enter. */
const PLACEHOLDER_ROTATION_INTERVAL_MS = PLACEHOLDER_PAUSE_MS + PLACEHOLDER_TRANSITION_MS;

const advanceIndex = (prevIndex: number): number => (prevIndex + 1) % rotatingTerms.length;

type Props = {
    shouldStopRotating: boolean;
};

export const SearchBarRotatingPlaceholder: FC<Props> = ({ shouldStopRotating }) => {
    const intl = useIntl();
    const [currentIndex, setCurrentIndex] = useState(randomIndex);
    // Start on company name so the overlay matches the native "Search 1stDibs"
    // placeholder, then animate into the first rotating term on mount.
    const [displayText, setDisplayText] = useState(() =>
        intl.formatMessage(searchBarMessages.companyName)
    );
    const [outgoingText, setOutgoingText] = useState<string | null>(null);
    // After closing the search overlay the term is already fully visible, so the
    // next change should wait only the pause — not a full exit+enter cycle.
    const resumeWithPauseOnlyRef = useRef(false);

    const isAnimating = outgoingText !== null;

    useEffect(() => {
        if (shouldStopRotating) {
            resumeWithPauseOnlyRef.current = true;
            return undefined;
        }

        const initialDelayMs = resumeWithPauseOnlyRef.current
            ? PLACEHOLDER_PAUSE_MS
            : PLACEHOLDER_ROTATION_INTERVAL_MS;
        resumeWithPauseOnlyRef.current = false;

        let intervalId: number | undefined;
        const timeoutId = window.setTimeout(() => {
            setCurrentIndex(advanceIndex);
            intervalId = window.setInterval(() => {
                setCurrentIndex(advanceIndex);
            }, PLACEHOLDER_ROTATION_INTERVAL_MS);
        }, initialDelayMs);

        return () => {
            window.clearTimeout(timeoutId);
            if (intervalId !== undefined) {
                window.clearInterval(intervalId);
            }
        };
    }, [shouldStopRotating]);

    useEffect(() => {
        const rotatingText = rotatingTerms[currentIndex];
        if (shouldStopRotating) {
            // Keep the current term; clear in-flight animation and sync display
            // so reopen does not replay fade on the same (or stale) text.
            setOutgoingText(null);
            setDisplayText(rotatingText);
            return;
        }
        if (rotatingText === displayText) {
            return;
        }
        setOutgoingText(displayText);
        setDisplayText(rotatingText);
    }, [shouldStopRotating, currentIndex, displayText]);

    const handleAnimationEnd = (event: AnimationEvent<HTMLSpanElement>): void => {
        if (event.target !== event.currentTarget) {
            return;
        }
        setOutgoingText(null);
    };

    if (shouldStopRotating) {
        return null;
    }

    return (
        <span
            aria-hidden
            className={classnames(
                styles.placeholderOverlay,
                dibsCss.absolute,
                dibsCss.top0,
                dibsCss.zBump,
                dibsCss.hFull,
                dibsCss.flex,
                dibsCss.itemsCenter,
                dibsCss.pointerEventsNone,
                dibsCss.textBodyMediumNormal,
                dibsCss.textSatan
            )}
        >
            {intl.formatMessage(searchBarMessages.search)}
            <span
                className={classnames(
                    dibsCss.grid,
                    dibsCss.flex1,
                    dibsCss.minW0,
                    dibsCss.overflowHidden
                )}
            >
                {isAnimating && (
                    <span
                        className={classnames(
                            styles.rotatingTerm,
                            styles.fadeSlideUp,
                            dibsCss.truncate
                        )}
                    >
                        {outgoingText}
                    </span>
                )}
                <span
                    className={classnames(styles.rotatingTerm, dibsCss.truncate, {
                        [styles.fadeSlideIn]: isAnimating,
                    })}
                    onAnimationEnd={handleAnimationEnd}
                >
                    {displayText}
                </span>
            </span>
        </span>
    );
};
